import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import { useLink } from "@made-people/grease-link";
import { useTranslate } from "@made-people/grease-translations";
import type { SbBlokData, ISbStoryData } from "@storyblok/js";
import { useFrontendContext } from "../../../hooks/use-frontend-context";

const formatPublishedAt = (publishedAt?: string | null, language?: string) => {
  if (!publishedAt) {
    return "";
  }

  const date = new Date(publishedAt);
  if (Number.isNaN(date.getTime())) {
    return publishedAt;
  }

  return new Intl.DateTimeFormat(language, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const BlogListItem = (props: { item: ISbStoryData }) => {
  const Link = useLink();
  const frontendContext = useFrontendContext();
  const translate = useTranslate();
  const { item } = props;
  const publishedAt = formatPublishedAt(
    item.first_published_at,
    frontendContext.language,
  );
  const hasMeta = item.content.blogAuthor || publishedAt;

  const imageSrc = item.content.blogImage?.filename;

  const title =
    item.content.pageContent.find(
      (b: SbBlokData) => b.component === "PageHeader",
    )?.textBlock_heading_heading ?? item.name;

  return (
    <Link href={`blog/${item.slug}`} className="group block focus:outline-none">
      <div className="flex h-full flex-col gap-5">
        {imageSrc ? (
          <ResponsiveImage
            initial={{
              src: imageSrc,
              alt: item.name,
            }}
            displaySize={{
              mobile: "100vw",
              tablet: "100vw",
              desktop: "50vw",
            }}
            className="aspect-[2/1] w-full overflow-hidden rounded-sm object-cover"
          />
        ) : (
          <div className="aspect-[2/1] w-full rounded-sm bg-neutral-100" />
        )}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-4">
          <h2 className="font-secondary text-heading-md transition-colors group-hover:underline">
            {title}
          </h2>
          {hasMeta ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm text-text-less-emphasis-1">
              {item.content.blogAuthor ? (
                <span>{item.content.blogAuthor}</span>
              ) : null}
              {publishedAt ? <span>{publishedAt}</span> : null}
            </div>
          ) : null}
          {item.content.blogSummary ? (
            <p
              className="text-body-sm text-neutral-700 lg:text-body-md"
              dangerouslySetInnerHTML={{
                __html: item.content.blogSummary.gearsResolvedRichText,
              }}
            />
          ) : null}
          <span className="mt-1 inline-flex items-center border border-black px-4 py-2 text-body-sm transition-colors group-hover:bg-black group-hover:text-white">
            {translate("Text_Block_Read_More")}
          </span>
        </div>
      </div>
    </Link>
  );
};
